import { useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from "react-redux";
import { Grid, CircularProgress, Typography } from "@mui/material";
import IconButton from '@mui/material/IconButton';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import GetAppIcon from '@mui/icons-material/GetApp';
import axios from 'axios';
import documentApi from "../../apis/documentApi";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { handleDownloadFileFromS3 } from "../../utilities/helpers";

const Attachment = ({ uploadFileParams }) => {
    const { inspectionUid, code } = uploadFileParams;
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);

    const tenantId = useSelector((state) => {
        return state.tenantId;
      }); 

    useEffect(() => {
      (async () => {
        try {
          setLoading(true);
          const res = await documentApi.get(`/documents/inspectionid/${inspectionUid}/referencecode/${code}`);
          setAttachments(res.data.body);
          setLoading(false);
        } catch (error) {
          console.error(error);
          setLoading(false);
        }
      })();
    }, [inspectionUid, code]);

    const getDocuments = async () => {
      const res = await documentApi.get(`/documents/inspectionid/${inspectionUid}/referencecode/${code}`);
      return res.data.body;
    }

    const handleUploadFile = async (event) => {
        const foundFile = event.target.files[0];
        setLoading(true);
        const fileUuid = uuidv4();
        try {
            //1. Create inspection exhibition first
            const downloadUrl = `/documents/presignedurldownload?objectKey=${code}/${fileUuid}`;
            try {
                await ieqipCoreServiceApi.post("/api/inspectionexhibitions", {
                    code: foundFile.name,
                    description: downloadUrl,
                    inspection_uid: inspectionUid
                });
            } catch (exhibitionError) {
                console.error('Failed to create inspection exhibition:', exhibitionError);
                setLoading(false);
                const errorMessage = exhibitionError.response?.data?.message || 
                                   exhibitionError.response?.data?.error ||
                                   exhibitionError.response?.data?.detail || 
                                   exhibitionError.message || 
                                   'Failed to create inspection exhibition. Please try again.';
                throw new Error(errorMessage);
            }

            //2. Get upload presigned url
            const s3PresignedUrlResult = await documentApi.post(`/documents/presignedurlupload`, {
                objectKey: `${code}/${fileUuid}`,
                contentType: foundFile.type
            });

            //3. Upload file to s3 using presigned url
            await axios.put(s3PresignedUrlResult.data.body, foundFile);

            //4. Insert record to document table in database
            await documentApi.post(`/documents`, {
                tenant_id: tenantId,
                document_uid: fileUuid,
                reference_code: code,
                inspection_uid: inspectionUid,
                name: foundFile.name
            });

            if (foundFile?.name) {
              setAttachments(await getDocuments());
            }

        } catch (error) {
            console.error('Upload failed:', error);
            // Show error message from API response
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               error.message || 
                               'Failed to upload file. Please try again.';
            alert(errorMessage);
        }
        setLoading(false);
    }

    const handleDownloadFile = async (attachment) => {
        setLoading(true);
        try {
            // Construct the object key using the reference code and document_uid
            const objectKey = `${code}/${attachment.document_uid}`;
            await handleDownloadFileFromS3(documentApi, objectKey, attachment.name, '/documents/presignedurldownload');
        } catch (error) {
            console.error('Download failed:', error);
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               error.response?.data?.detail || 
                               error.message || 
                               'Failed to download file. Please try again.';
            alert(errorMessage);
        }
        setLoading(false);
    }

    const handleDeleteFile = async (attachment) => {
        setLoading(true);
        try {
            // Delete the document
            await documentApi.delete(`/documents/${attachment.document_uid}`);

            // Delete the inspection exhibition
            const exhibitions = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspectionexhibitions`);
            const exhibition = exhibitions.data.find(e => e.code === attachment.name);
            if (exhibition) {
                await ieqipCoreServiceApi.delete(`/api/inspectionexhibitions/${exhibition.inspection_exhibition_uid}`);
            }

            setAttachments(await getDocuments());
        } catch (error) {
            console.error('Delete failed', error);
            setLoading(false);
        }
        setLoading(false);
    }

    return <>
        <Grid item xs={12}>
            <input id="icon-button-file" type="file" onChange={handleUploadFile} />
        </Grid>
        {loading && <CircularProgress />}
        <Grid item xs={12}>
            <Grid container item>
                {attachments && attachments.map(attachment => {
                    return <>
                        <Grid key={attachment.id} item xs={9}>
                            <Typography
                                component="span"
                                sx={{
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'primary.dark'
                                    }
                                }}
                                onClick={() => handleDownloadFile(attachment)}
                            >
                                {attachment.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <IconButton color="secondary" aria-label="delete file" component="span" onClick={() => handleDeleteFile(attachment)}>
                                <DeleteForeverIcon />
                            </IconButton>
                            <IconButton color="secondary" aria-label="download file" component="span" onClick={() => handleDownloadFile(attachment)}>
                                <GetAppIcon />
                            </IconButton>
                        </Grid>
                    </>
                })}
            </Grid>
        </Grid>
    </>
}

export default Attachment;